import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  BookOpen,
  Plus,
  Trash2,
  Save,
  Users,
  Search,
  History,
  ChevronRight,
  FileText,
  Download,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const HistoricalGradesTab = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [customYear, setCustomYear] = useState('');
  const [showCustomYearInput, setShowCustomYearInput] = useState(false);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showGradeDialog, setShowGradeDialog] = useState(false);
  const [gradeForm, setGradeForm] = useState({
    subject: '',
    grade_value: '',
    grade_system: 'percentage',
    max_points: '100',
    description: '',
    notes: ''
  });
  const [gradingSystem, setGradingSystem] = useState('percentage');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    fetchStudents();
    fetchGradingSystem();
  }, []);

  useEffect(() => {
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      setFilteredStudents(students.filter(s =>
        `${s.first_name} ${s.last_name_paternal} ${s.last_name_maternal || ''}`.toLowerCase().includes(term) ||
        s.student_number?.toLowerCase().includes(term) ||
        s.assigned_grade?.toLowerCase().includes(term)
      ));
    } else {
      setFilteredStudents(students);
    }
  }, [searchTerm, students]);

  useEffect(() => {
    if (selectedStudent && selectedYear) {
      fetchStudentGrades();
    }
  }, [selectedStudent, selectedYear]);

  const fetchStudents = async () => {
    try {
      const response = await axios.get('/students');
      setStudents(response.data);
      setFilteredStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchGradingSystem = async () => {
    try {
      const response = await axios.get('/settings/grading-system');
      const system = response.data.grading_system || 'percentage';
      setGradingSystem(system);
      setGradeForm(prev => ({ ...prev, grade_system: system }));
    } catch {
      setGradingSystem('percentage');
    }
  };

  const fetchStudentGrades = async () => {
    if (!selectedStudent || !selectedYear) return;
    try {
      const response = await axios.get(`/grades/student/${selectedStudent.id}?academic_year=${encodeURIComponent(selectedYear)}`);
      setGrades(response.data.grades || []);
    } catch (error) {
      console.error('Error fetching grades:', error);
      setGrades([]);
    }
  };

  const handleAddGrade = async (e) => {
    e.preventDefault();
    if (!selectedStudent || !selectedYear) return;
    setLoading(true);
    try {
      await axios.post('/grades/historical', {
        student_id: selectedStudent.id,
        subject: gradeForm.subject,
        grade_value: parseFloat(gradeForm.grade_value),
        grade_system: gradeForm.grade_system,
        max_points: gradeForm.max_points ? parseFloat(gradeForm.max_points) : null,
        description: gradeForm.description || null,
        notes: gradeForm.notes || null,
        academic_year: selectedYear
      });
      toast.success('Calificacion historica registrada');
      setShowGradeDialog(false);
      setGradeForm({
        subject: '',
        grade_value: '',
        grade_system: gradingSystem,
        max_points: '100',
        description: '',
        notes: ''
      });
      fetchStudentGrades();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar calificacion');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGrade = async (gradeId) => {
    try {
      await axios.delete(`/grades/historical/${gradeId}`);
      toast.success('Calificacion eliminada');
      fetchStudentGrades();
    } catch (error) {
      toast.error('Error al eliminar calificacion');
    }
  };

  const handleDownloadTranscript = async () => {
    if (!selectedStudent) return;
    try {
      const response = await axios.get(`/grades/transcript/${selectedStudent.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transcripcion_${selectedStudent.student_number || selectedStudent.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Transcripcion descargada');
    } catch (error) {
      toast.error('Error al generar la transcripcion');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get('/grades/historical/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'template_calificaciones_historicas.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template descargado');
    } catch {
      toast.error('Error al descargar template');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/grades/historical/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} calificaciones importadas`);
        if (selectedStudent && selectedYear) fetchStudentGrades();
      }
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} filas con errores`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar');
    } finally {
      setImportLoading(false);
      e.target.value = '';
    }
  };

  const handleYearSelect = (value) => {
    if (value === '__custom__') {
      setShowCustomYearInput(true);
      return;
    }
    setSelectedYear(value);
    setShowCustomYearInput(false);
  };

  const handleCustomYearConfirm = () => {
    if (customYear.trim()) {
      setSelectedYear(customYear.trim());
      setShowCustomYearInput(false);
      setCustomYear('');
    }
  };

  // Generate year options (current year going back 10 years)
  const currentCalendarYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = 0; i < 10; i++) {
    const y = currentCalendarYear - i;
    yearOptions.push(`${y - 1}-${y}`);
  }

  const getLetterGrade = (value, maxPoints) => {
    const mp = maxPoints || (value > 10 ? 100 : 10);
    const pct = (value / mp) * 100;
    if (pct >= 90) return 'A';
    if (pct >= 80) return 'B';
    if (pct >= 70) return 'C';
    if (pct >= 60) return 'D';
    return 'F';
  };

  const getGradeColor = (letter) => {
    const colors = { A: 'bg-green-100 text-green-800', B: 'bg-blue-100 text-blue-800', C: 'bg-yellow-100 text-yellow-800', D: 'bg-orange-100 text-orange-800', F: 'bg-red-100 text-red-800' };
    return colors[letter] || 'bg-gray-100 text-gray-800';
  };

  // Step 1: No student selected - show student list
  if (!selectedStudent) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-1">Historial Academico</h2>
            <p className="text-slate-500">Registra calificaciones de anos anteriores para los estudiantes</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="text-green-600 border-green-200 hover:bg-green-50"
              data-testid="download-template-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              Descargar Template
            </Button>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleImportFile}
                disabled={importLoading}
                data-testid="import-file-input"
              />
              <Button
                variant="outline"
                size="sm"
                className="text-blue-600 border-blue-200 hover:bg-blue-50 pointer-events-none"
                disabled={importLoading}
                asChild
              >
                <span>
                  <Upload className="w-4 h-4 mr-1" />
                  {importLoading ? 'Importando...' : 'Importar Excel'}
                </span>
              </Button>
            </label>
          </div>
        </div>

        {/* Import result */}
        {importResult && (
          <Card className={importResult.errors?.length > 0 ? 'border-yellow-300 bg-yellow-50' : 'border-green-300 bg-green-50'}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                {importResult.errors?.length > 0 ? (
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                ) : (
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-slate-800">
                    {importResult.imported} calificaciones importadas de {importResult.total_rows} filas
                  </p>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-sm font-medium text-yellow-700">Errores:</p>
                      {importResult.errors.map((err, i) => (
                        <p key={i} className="text-sm text-yellow-600">{err}</p>
                      ))}
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setImportResult(null)} className="text-slate-400">
                  &times;
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="modern-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Seleccionar Estudiante
            </CardTitle>
            <CardDescription>Busca y selecciona el estudiante para ingresar sus notas historicas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, numero o grado..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="historical-search-input"
                />
              </div>
            </div>

            <div className="divide-y max-h-[500px] overflow-y-auto">
              {filteredStudents.length === 0 ? (
                <p className="text-center py-8 text-slate-400">No se encontraron estudiantes</p>
              ) : (
                filteredStudents.map((student) => (
                  <div
                    key={student.id}
                    className="flex items-center justify-between py-3 px-2 hover:bg-slate-50 cursor-pointer rounded-lg transition-colors"
                    onClick={() => setSelectedStudent(student)}
                    data-testid={`student-row-${student.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm">
                        {student.first_name?.charAt(0)}{student.last_name_paternal?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">
                          {student.first_name} {student.last_name_paternal} {student.last_name_maternal || ''}
                        </p>
                        <p className="text-sm text-slate-400">
                          {student.student_number} - {student.assigned_grade}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Student selected - show year selection + grades
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 mb-1">Historial Academico</h2>
          <p className="text-slate-500">Registra calificaciones de anos anteriores</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleDownloadTranscript}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
            data-testid="download-transcript-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Generar Transcripcion
          </Button>
          <Button variant="outline" onClick={() => { setSelectedStudent(null); setSelectedYear(''); setGrades([]); }} data-testid="back-to-students-btn">
            Volver a Estudiantes
          </Button>
        </div>
      </div>

      {/* Student Info */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-blue-200 flex items-center justify-center text-blue-800 font-bold text-lg">
              {selectedStudent.first_name?.charAt(0)}{selectedStudent.last_name_paternal?.charAt(0)}
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                {selectedStudent.first_name} {selectedStudent.last_name_paternal} {selectedStudent.last_name_maternal || ''}
              </h3>
              <p className="text-sm text-slate-500">
                {selectedStudent.student_number} - Grado actual: {selectedStudent.assigned_grade}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Year Selection */}
      <Card className="modern-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-blue-600" />
            Ano Academico
          </CardTitle>
          <CardDescription>Selecciona el ano academico para registrar o ver calificaciones</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label>Ano Academico</Label>
              <Select value={selectedYear} onValueChange={handleYearSelect}>
                <SelectTrigger data-testid="year-select">
                  <SelectValue placeholder="Seleccionar ano..." />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((year) => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">Otro ano...</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {showCustomYearInput && (
              <div className="flex items-end gap-2">
                <div>
                  <Label>Ano personalizado</Label>
                  <Input
                    placeholder="Ej: 2018-2019"
                    value={customYear}
                    onChange={(e) => setCustomYear(e.target.value)}
                    data-testid="custom-year-input"
                  />
                </div>
                <Button onClick={handleCustomYearConfirm} data-testid="confirm-custom-year-btn">Confirmar</Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Grades for selected year */}
      {selectedYear && (
        <Card className="modern-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  Calificaciones - {selectedYear}
                </CardTitle>
                <CardDescription>
                  {grades.length} calificacion(es) registrada(s) para este ano
                </CardDescription>
              </div>
              <Button
                onClick={() => {
                  setGradeForm({
                    subject: '',
                    grade_value: '',
                    grade_system: gradingSystem,
                    max_points: gradingSystem === 'percentage' ? '100' : (gradingSystem === 'points' ? '4' : ''),
                    description: '',
                    notes: ''
                  });
                  setShowGradeDialog(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-historical-grade-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar Calificacion
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {grades.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <History className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-lg font-medium">Sin calificaciones para {selectedYear}</p>
                <p className="text-sm mt-1">Haz clic en "Agregar Calificacion" para comenzar</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Materia</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Evaluacion</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Calificacion</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Nota</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Anotaciones</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {grades.map((grade) => {
                      const letter = getLetterGrade(grade.grade_value, grade.max_points);
                      return (
                        <tr key={grade.id} className="hover:bg-gray-50" data-testid={`historical-grade-${grade.id}`}>
                          <td className="px-4 py-3 font-medium text-slate-800">{grade.subject}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{grade.description || '-'}</td>
                          <td className="px-4 py-3">
                            <Badge className={getGradeColor(letter)}>
                              {grade.grade_value}/{grade.max_points || 100} ({letter})
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={getGradeColor(letter)}>{letter}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 max-w-[200px] truncate">
                            {grade.notes || '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteGrade(grade.id)}
                              data-testid={`delete-grade-${grade.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Grade Dialog */}
      <Dialog open={showGradeDialog} onOpenChange={setShowGradeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar Calificacion Historica</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddGrade} className="space-y-4">
            <div>
              <Label>Materia *</Label>
              <Input
                value={gradeForm.subject}
                onChange={(e) => setGradeForm({ ...gradeForm, subject: e.target.value })}
                placeholder="Ej: Matematicas, Espanol, Ciencias..."
                required
                data-testid="historical-subject-input"
              />
            </div>

            <div>
              <Label>Descripcion de la Evaluacion</Label>
              <Input
                value={gradeForm.description}
                onChange={(e) => setGradeForm({ ...gradeForm, description: e.target.value })}
                placeholder="Ej: Primer Semestre, Nota Final..."
                data-testid="historical-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Calificacion *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={gradeForm.grade_value}
                  onChange={(e) => setGradeForm({ ...gradeForm, grade_value: e.target.value })}
                  placeholder={gradingSystem === 'points' ? '0-4' : '0-100'}
                  required
                  data-testid="historical-grade-value-input"
                />
              </div>
              <div>
                <Label>Puntos Maximos</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={gradeForm.max_points}
                  onChange={(e) => setGradeForm({ ...gradeForm, max_points: e.target.value })}
                  placeholder="100"
                  data-testid="historical-max-points-input"
                />
              </div>
            </div>

            <div>
              <Label>Anotaciones</Label>
              <Textarea
                value={gradeForm.notes}
                onChange={(e) => setGradeForm({ ...gradeForm, notes: e.target.value })}
                placeholder="Observaciones sobre el desempeno del estudiante..."
                data-testid="historical-notes-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowGradeDialog(false)}>Cancelar</Button>
              <Button type="submit" disabled={loading} data-testid="save-historical-grade-btn">
                {loading ? 'Guardando...' : 'Guardar Calificacion'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HistoricalGradesTab;
